import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";

function getRelatedPosts(currentPost, allPosts) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Only link to posts that were live on or before the current post's own publish date,
  // so readers visiting post A never see links to posts that didn't exist when A went live.
  const cutoff = currentPost.publishDate ? new Date(currentPost.publishDate) : today;

  const currentKeywords = (currentPost.keywords || "").toLowerCase().split(",").map((k) => k.trim());

  return allPosts
    .filter((p) => {
      if (p.id === currentPost.id) return false;
      const postDate = p.publishDate ? new Date(p.publishDate) : null;
      return postDate && postDate <= cutoff && postDate <= today;
    })
    .map((p) => {
      const postKeywords = (p.keywords || "").toLowerCase().split(",").map((k) => k.trim());
      const overlap = currentKeywords.filter((k) => postKeywords.includes(k)).length;
      return { ...p, overlap };
    })
    .sort((a, b) => b.overlap - a.overlap || new Date(b.publishDate) - new Date(a.publishDate))
    .slice(0, 3);
}

function BlogPost() {
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { articleId } = useParams();

  useEffect(() => {
    fetch("/blogPosts.json")
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const article = data.find(
          (p) => p.id === articleId && (!p.publishDate || new Date(p.publishDate) <= today)
        );
        setPost(article || null);
        if (article) setRelatedPosts(getRelatedPosts(article, data));
        setLoading(false);
      })
      .catch((error) => {
        console.error("Fetching error:", error);
        setLoading(false);
      });
  }, [articleId]);

  if (loading) return <div className="max-w-[640px] mx-auto px-4 py-8">Loading...</div>;
  if (!post) return <div className="max-w-[640px] mx-auto px-4 py-8">Post not found.</div>;

  return (
    <div className='max-w-[640px] mx-auto blog-post'>
      <Helmet>
        <title>{post.title} | tastebud.ee</title>
        <meta name='description' content={post.description} />
        <link rel='canonical' href={`https://tastebud.ee/blog/${post.id}`} />
        <meta property='og:title' content={post.title} />
        <meta property='og:description' content={post.description} />
        <meta property='og:url' content={`https://tastebud.ee/blog/${post.id}`} />
        <meta property='og:type' content='article' />
      </Helmet>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.description,
            "datePublished": post.publishDate,
            "dateModified": post.publishDate,
            "publisher": { "@type": "Organization", "name": "tastebud.ee", "url": "https://tastebud.ee" },
            "mainEntityOfPage": { "@type": "WebPage", "@id": `https://tastebud.ee/blog/${post.id}` },
          }),
        }}
      />
      <h1 className='px-4 my-8 text-center text-3xl'>{post.title}</h1>
      <h3 className='subtitle'>{post.description}</h3>
      <div dangerouslySetInnerHTML={{ __html: post.content }} />

      {relatedPosts.length > 0 && (
        <div className='mt-12 mb-10 px-4 border-t border-gray-200 pt-8'>
          <h2 className='text-xl font-semibold mb-4'>Related articles</h2>
          <ul className='space-y-4'>
            {relatedPosts.map((p) => (
              <li key={p.id}>
                <Link to={`/blog/${p.id}`} className='text-base font-medium hover:underline'>
                  {p.title}
                </Link>
                <p className='text-sm text-gray-500 mt-0.5'>{p.description}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default BlogPost;
